import { I18n, useI18n } from '../hooks/I18n';
import { PathData as FetchedPathData, useData } from '../hooks/UseData';
import { defined, Optional, returnIf } from '../utils/Util';
import { PathData } from './PathModel';
import { RaceData } from './RaceDataUtil';

const initials = 'AGMTあかさたなはまやらわん';

class LocalizedId {
  constructor(readonly id: string, readonly displayName: string) {}
}

class LocalizedIdWithKana extends LocalizedId {
  constructor(id: string, displayName: string, readonly kana: string) {
    super(id, displayName);
  }
}

export class LocalizedIdWithPath extends LocalizedId {
  constructor(id: string, displayName: string, readonly path: string) {
    super(id, displayName);
  }
}

export class ParentPathModel {
  private idWithPaths: LocalizedIdWithPath[];
  private paths: string[];
  private pathToDisplayNameMap: Record<string, LocalizedIdWithPath>;

  constructor(readonly groups: ItemGroup[]) {
    this.idWithPaths = this.flatten();
    this.paths = this.idWithPaths.map((i) => i.path);
    this.pathToDisplayNameMap = Object.fromEntries(
      this.idWithPaths.map((i) => [i.path, i])
    );
  }

  private flatten(): LocalizedIdWithPath[] {
    return this.groups.reduce<LocalizedIdWithPath[]>((items, { subGroups }) => {
      const i = subGroups.reduce<LocalizedIdWithPath[]>(
        (items, subGroup) => [...items, ...subGroup.items],
        []
      );
      return [...items, ...i];
    }, []);
  }

  getLocalizedIdWithPath(): LocalizedIdWithPath[] {
    return this.idWithPaths;
  }

  getPrevPath(path: string): Optional<LocalizedIdWithPath> {
    const index = this.paths.indexOf(path);
    const prevPath = this.paths[index - 1];
    return returnIf(prevPath, (p) => this.pathToDisplayNameMap[p]);
  }

  getNextPath(path: string): Optional<LocalizedIdWithPath> {
    const index = this.paths.indexOf(path);
    const nextPath = this.paths[index + 1];
    return returnIf(nextPath, (p) => this.pathToDisplayNameMap[p]);
  }
}

class ItemGroup {
  constructor(
    readonly groupId: LocalizedId,
    readonly subGroups: ItemSubGroup[]
  ) {}
}
class ItemSubGroup {
  readonly items: LocalizedIdWithPath[];
  constructor(
    readonly subGroupId: LocalizedId,
    items: LocalizedId[],
    parentPath: string
  ) {
    this.items = items.map((item) => ({ ...item, path: parentPath + item.id }));
  }
}

class ParentPathViewData {
  constructor(
    private readonly initials: string,
    // /people/people/
    private readonly parentPath: string,
    // aaa, bbb, ccc
    private readonly paths: string[]
  ) {}

  private createListItems(
    i18n: I18n,
    raceData: RaceData
  ): LocalizedIdWithKana[] {
    return (
      this.paths
        .map((path) => {
          const { displayName, kana } = new PathData(
            this.parentPath,
            path
          ).localizeDirName(i18n, raceData);
          return new LocalizedIdWithKana(path, displayName, kana);
        })
        // 五十音順にするためソートする
        .sort((a, b) => a.kana.localeCompare(b.kana))
    );
  }

  private createGroupsData(
    listItems: LocalizedIdWithKana[]
  ): [string, string[]][] {
    const initialsOfItems = Array.from(
      new Set(listItems.map((item) => item.kana[0]))
    );
    return [...this.initials].reduce<[string, string[]][]>(
      (groups, groupInitial, index, array) => {
        // 末端の文字に対応するグループは作らない
        const nextInitial = array[index + 1];
        if (!defined(nextInitial)) {
          return groups;
        }

        // グループに含まれるイニシャル
        const rangeSize =
          nextInitial.charCodeAt(0) - groupInitial.charCodeAt(0);
        const subGroupInitials: string[] = [...Array(rangeSize).keys()]
          .map((num) => String.fromCharCode(groupInitial.charCodeAt(0) + num))
          // そのイニシャルで始まる要素がなければ作らない
          .filter((char) => initialsOfItems.includes(char));

        // 空のグループは表示しない
        if (subGroupInitials.length === 0) {
          return groups;
        }
        return [...groups, [groupInitial, subGroupInitials]];
      },
      []
    );
  }

  getInitialGroups(i18n: I18n, raceData: RaceData): ItemGroup[] {
    const listItems = this.createListItems(i18n, raceData);
    const groups = this.createGroupsData(listItems);

    const itemGroups: ItemGroup[] = [];
    let otherGroupItems = [...listItems];
    for (const [groupInitial, subGroupInitials] of groups) {
      // IDは文字コードを使う。URLのハッシュになるため
      const groupId = new LocalizedId(
        String(groupInitial.charCodeAt(0)),
        i18n.LABELS.localize('list_index', undefined, {
          initial: groupInitial,
        })
      );
      const itemGroup = new ItemGroup(groupId, []);
      itemGroups.push(itemGroup);
      for (const subGroupInitial of subGroupInitials) {
        const subGroupId = new LocalizedId(
          String(subGroupInitial.charCodeAt(0)),
          subGroupInitial
        );
        const items = listItems.filter((item) =>
          item.kana.startsWith(subGroupInitial)
        );
        // その他のグループに入る要素の候補を更新
        otherGroupItems = otherGroupItems.filter(
          (item) => !items.includes(item)
        );
        itemGroup.subGroups.push(
          new ItemSubGroup(subGroupId, items, this.parentPath)
        );
      }
    }
    if (otherGroupItems.length > 0) {
      itemGroups.push(
        new ItemGroup(
          new LocalizedId('list_others', i18n.LABELS.localize('list_others')),
          [
            new ItemSubGroup(
              new LocalizedId('', ''),
              otherGroupItems,
              this.parentPath
            ),
          ]
        )
      );
    }
    return itemGroups;
  }
}

const dataMap: Map<string, ParentPathModel> = new Map();

const fetchPaths = (
  parentPath: string,
  pathData: FetchedPathData
): string[] => {
  let paths: Optional<string | FetchedPathData> = pathData;
  for (const segment of parentPath.split('/')) {
    if (segment === '') {
      continue;
    }
    paths = typeof paths === 'object' ? paths[segment] : undefined;
  }
  if (typeof paths === 'object') {
    return Object.keys(paths).filter((p) => typeof p === 'string');
  } else {
    return [];
  }
};

export const useParentPathModel = (parentPath: string): ParentPathModel => {
  // TODO: スラッシュありとなしが混在
  // /people/people/
  const i18n = useI18n();
  const { fetchRaceData, fetchPathData } = useData();
  const raceData = fetchRaceData();
  const pathData = fetchPathData();
  let cachedData = dataMap.get(parentPath);
  if (!defined(cachedData)) {
    const paths = fetchPaths(parentPath, pathData);
    cachedData = new ParentPathModel(
      new ParentPathViewData(initials, parentPath, paths).getInitialGroups(
        i18n,
        raceData
      )
    );
    dataMap.set(parentPath, cachedData);
  }
  return cachedData;
};
